import { createElement, useState } from "react";
import { router } from "@inertiajs/react";
import {
  CircleStackIcon,
  ExclamationTriangleIcon,
  ArchiveBoxArrowDownIcon,
} from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface ChannelStat {
  channel: string;
  threads: number;
  median_mb: number;
  avg_mb: number;
  max_mb: number;
}

interface FatThread {
  thread_id: string;
  size_mb: number;
  status: string | null;
  busy: boolean;
  chat_type: string | null;
  project_id: number | null;
}

interface TableSize {
  table_name: string;
  estimated_rows: number;
  total_size: string;
}

interface CheckpointHealthProps {
  fat_threshold_mb: number;
  channel_stats: ChannelStat[];
  fat_threads: FatThread[];
  table_sizes: TableSize[];
}

function StatusBadge({ status, busy }: { status: string | null; busy: boolean }) {
  const cls = busy
    ? "bg-amber-100 text-amber-800"
    : status
      ? "bg-slate-100 text-slate-700"
      : "bg-slate-50 text-slate-400";
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status ?? "—"}
    </span>
  );
}

function CompactButton({ thread }: { thread: FatThread }) {
  const [submitting, setSubmitting] = useState(false);

  const onClick = () => {
    if (
      !window.confirm(
        `Enqueue a compaction run for thread ${thread.thread_id}?\n\n` +
          `This rewrites the thread's history (summary + recent turns). ` +
          `The full pre-compaction record stays in conversation traces.`,
      )
    ) {
      return;
    }
    setSubmitting(true);
    router.post(
      "/admin/checkpoint_health/compact",
      { thread_id: thread.thread_id },
      { preserveScroll: true, onFinish: () => setSubmitting(false) },
    );
  };

  return (
    <button
      onClick={onClick}
      disabled={submitting || thread.busy}
      title={thread.busy ? "Thread is mid-run — skipped automatically" : "Enqueue compaction run"}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
        bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <ArchiveBoxArrowDownIcon className="w-4 h-4" />
      {submitting ? "Enqueuing…" : "Compact"}
    </button>
  );
}

function CheckpointHealthIndex({
  fat_threshold_mb,
  channel_stats,
  fat_threads,
  table_sizes,
}: CheckpointHealthProps) {
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <CircleStackIcon className="w-7 h-7" />
          Checkpoint Health
        </h1>
        <p className="text-muted-foreground mt-1">
          Blob sizes at each thread's latest checkpoint. Compaction shrinks the{" "}
          <code className="font-mono text-xs">messages</code> channel; the nightly pruner reaps
          the historical versions across blobs and writes. Fat threshold:{" "}
          <span className="font-medium">{fat_threshold_mb} MB</span>.
        </p>
      </header>

      {/* Table footprints — the pruner's domain. */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">Table footprints</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {table_sizes.map((t) => (
            <div key={t.table_name} className="rounded-xl border border-border bg-card p-4">
              <div className="text-sm font-mono text-muted-foreground">{t.table_name}</div>
              <div className="text-2xl font-bold text-foreground mt-1">{t.total_size}</div>
              <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                ~{t.estimated_rows.toLocaleString()} rows
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Per-channel blob sizes over latest checkpoints. */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-3">By channel (latest checkpoint)</h2>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Channel</th>
                <th className="text-right px-4 py-2 font-medium">Threads</th>
                <th className="text-right px-4 py-2 font-medium">Median MB</th>
                <th className="text-right px-4 py-2 font-medium">Avg MB</th>
                <th className="text-right px-4 py-2 font-medium">Max MB</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {channel_stats.map((c) => (
                <tr key={c.channel}>
                  <td className="px-4 py-2 font-mono">{c.channel}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{c.threads.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{c.median_mb.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{c.avg_mb.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{c.max_mb.toFixed(2)}</td>
                </tr>
              ))}
              {channel_stats.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                    No checkpoints found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Actionable fat-thread list with per-thread compaction triggers. */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          Fat threads
          {fat_threads.length > 0 && (
            <span className="inline-flex items-center gap-1 text-sm font-normal text-amber-700">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {fat_threads.length} over threshold
            </span>
          )}
        </h2>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Thread</th>
                <th className="text-right px-4 py-2 font-medium">messages MB</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Context</th>
                <th className="text-right px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {fat_threads.map((t) => (
                <tr key={t.thread_id}>
                  <td className="px-4 py-2 font-mono text-xs">{t.thread_id}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{t.size_mb.toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={t.status} busy={t.busy} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {t.chat_type ? (
                      <span>
                        {t.chat_type}
                        {t.project_id ? ` · project ${t.project_id}` : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <CompactButton thread={t} />
                  </td>
                </tr>
              ))}
              {fat_threads.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No threads over the {fat_threshold_mb} MB threshold. 🎉
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

CheckpointHealthIndex.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default CheckpointHealthIndex;
